/* tslint:disable */
/* eslint-disable */
/**
 * CyberWell Aegis API
 * Multi-tenant case management API with JWT auth, RBAC (Casbin), audit logging, and tenant isolation.
 *
 * The version of the OpenAPI document: v1
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { CaseEntityDto } from './CaseEntityDto';
import {
    CaseEntityDtoFromJSON,
    CaseEntityDtoFromJSONTyped,
    CaseEntityDtoToJSON,
    CaseEntityDtoToJSONTyped,
} from './CaseEntityDto';
import type { CaseSourceDto } from './CaseSourceDto';
import {
    CaseSourceDtoFromJSON,
    CaseSourceDtoFromJSONTyped,
    CaseSourceDtoToJSON,
    CaseSourceDtoToJSONTyped,
} from './CaseSourceDto';
import type { CreateCaseEventRequest } from './CreateCaseEventRequest';
import {
    CreateCaseEventRequestFromJSON,
    CreateCaseEventRequestFromJSONTyped,
    CreateCaseEventRequestToJSON,
    CreateCaseEventRequestToJSONTyped,
} from './CreateCaseEventRequest';

/**
 * Case update request containing fields to update.
 * 
 * **Updatable Fields:**
 * - `name`: Case name/title
 * - `summary`: Detailed case description
 * - `severity`: Case severity level (CRITICAL, HIGH, MEDIUM, LOW)
 * - `status`: Case status (OPEN, IN_PROGRESS, RESOLVED, CLOSED)
 * - `owner`: Case owner/assignee
 * - `escalated`: Whether the case has been escalated
 * - `entities`: List of entities associated with the case
 * - `custom_fields`: Custom fields for the case
 * - `sources`: List of sources for this case
 * - `correlation_group`: Group for linking related cases
 * - `correlation_metadata`: Metadata for case correlation
 * - `source_id`: Source ID from external system
 * - `source_technology`: Source technology that generated this case
 * 
 * **Field-Level Permissions:**
 * - Status changes require cases:update:status or cases:update:all
 * - Basic updates require cases:update:basic or cases:update:all
 * - Assignment changes require cases:update:assignment or cases:update:all
 * 
 * @export
 * @interface UpdateCaseRequest
 */
export interface UpdateCaseRequest {
    /**
     * Name/title of the case
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    name?: string;
    /**
     * Detailed summary of the case
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    summary?: string;
    /**
     * Severity level of the case
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    severity?: UpdateCaseRequestSeverityEnum;
    /**
     * Status of the case
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    status?: UpdateCaseRequestStatusEnum;
    /**
     * Owner/assignee of the case
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    owner?: string;
    /**
     * Whether the case has been escalated
     * @type {boolean}
     * @memberof UpdateCaseRequest
     */
    escalated?: boolean;
    /**
     * List of entities associated with the case
     * @type {Array<CaseEntityDto>}
     * @memberof UpdateCaseRequest
     */
    entities?: Array<CaseEntityDto>;
    /**
     * Custom fields for the case
     * @type {{ [key: string]: any; }}
     * @memberof UpdateCaseRequest
     */
    customFields?: { [key: string]: any; };
    /**
     * List of sources for this case
     * @type {Array<CaseSourceDto>}
     * @memberof UpdateCaseRequest
     */
    sources?: Array<CaseSourceDto>;
    /**
     * Correlation group for linking related cases
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    correlationGroup?: string;
    /**
     * Correlation metadata for linking related cases
     * @type {{ [key: string]: any; }}
     * @memberof UpdateCaseRequest
     */
    correlationMetadata?: { [key: string]: any; };
    /**
     * General case metadata for flexible storage of case-specific information
     * @type {{ [key: string]: any; }}
     * @memberof UpdateCaseRequest
     */
    metadata?: { [key: string]: any; };
    /**
     * Source ID from external system
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    sourceId?: string;
    /**
     * Source technology that generated this case
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    sourceTechnology?: string;
    /**
     * Case creation time (if not provided, uses current time)
     * @type {Date}
     * @memberof UpdateCaseRequest
     */
    created?: Date;
    /**
     * Original creation time from external system (for synchronization)
     * @type {Date}
     * @memberof UpdateCaseRequest
     */
    originalCreatedTime?: Date;
    /**
     * Assignment time (when case was assigned to owner)
     * @type {Date}
     * @memberof UpdateCaseRequest
     */
    assignedTime?: Date;
    /**
     * Close time (when case was closed/resolved)
     * @type {Date}
     * @memberof UpdateCaseRequest
     */
    closedTime?: Date;
    /**
     * Escalation time (when case was escalated)
     * @type {Date}
     * @memberof UpdateCaseRequest
     */
    escalatedTime?: Date;
    /**
     * Notification SLA timer value
     * @type {number}
     * @memberof UpdateCaseRequest
     */
    notifySLATimer?: number;
    /**
     * Detection SLA timer value
     * @type {number}
     * @memberof UpdateCaseRequest
     */
    detectionSLATimer?: number;
    /**
     * Assignment SLA timer value
     * @type {number}
     * @memberof UpdateCaseRequest
     */
    assignmentSLATimer?: number;
    /**
     * Remediation SLA timer value
     * @type {number}
     * @memberof UpdateCaseRequest
     */
    remediationSLATimer?: number;
    /**
     * Duration SLA timer value
     * @type {number}
     * @memberof UpdateCaseRequest
     */
    durationSLATimer?: number;
    /**
     * Close reason details (for cases updated to RESOLVED status)
     * @type {{ [key: string]: any; }}
     * @memberof UpdateCaseRequest
     */
    closeReason?: { [key: string]: any; };
    /**
     * Close rationale/justification (for cases updated to RESOLVED status)
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    closeRationale?: string;
    /**
     * Feedback about the case handling process
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    feedback?: string;
    /**
     * Creator of timeline events (if not provided, uses authenticated user)
     * @type {string}
     * @memberof UpdateCaseRequest
     */
    timelineEventCreator?: string;
    /**
     * List of events/notes for the case (for complete case updates)
     * @type {Array<CreateCaseEventRequest>}
     * @memberof UpdateCaseRequest
     */
    events?: Array<CreateCaseEventRequest>;
}


/**
 * @export
 */
export const UpdateCaseRequestSeverityEnum = {
    Critical: 'CRITICAL',
    High: 'HIGH',
    Medium: 'MEDIUM',
    Low: 'LOW'
} as const;
export type UpdateCaseRequestSeverityEnum = typeof UpdateCaseRequestSeverityEnum[keyof typeof UpdateCaseRequestSeverityEnum];

/**
 * @export
 */
export const UpdateCaseRequestStatusEnum = {
    Open: 'OPEN',
    InProgress: 'IN_PROGRESS',
    Resolved: 'RESOLVED',
    Closed: 'CLOSED'
} as const;
export type UpdateCaseRequestStatusEnum = typeof UpdateCaseRequestStatusEnum[keyof typeof UpdateCaseRequestStatusEnum];


/**
 * Check if a given object implements the UpdateCaseRequest interface.
 */
export function instanceOfUpdateCaseRequest(value: object): value is UpdateCaseRequest {
    return true;
}

export function UpdateCaseRequestFromJSON(json: any): UpdateCaseRequest {
    return UpdateCaseRequestFromJSONTyped(json, false);
}

export function UpdateCaseRequestFromJSONTyped(json: any, ignoreDiscriminator: boolean): UpdateCaseRequest {
    if (json == null) {
        return json;
    }
    return {
        
        'name': json['name'] == null ? undefined : json['name'],
        'summary': json['summary'] == null ? undefined : json['summary'],
        'severity': json['severity'] == null ? undefined : json['severity'],
        'status': json['status'] == null ? undefined : json['status'],
        'owner': json['owner'] == null ? undefined : json['owner'],
        'escalated': json['escalated'] == null ? undefined : json['escalated'],
        'entities': json['entities'] == null ? undefined : ((json['entities'] as Array<any>).map(CaseEntityDtoFromJSON)),
        'customFields': json['custom_fields'] == null ? undefined : json['custom_fields'],
        'sources': json['sources'] == null ? undefined : ((json['sources'] as Array<any>).map(CaseSourceDtoFromJSON)),
        'correlationGroup': json['correlation_group'] == null ? undefined : json['correlation_group'],
        'correlationMetadata': json['correlation_metadata'] == null ? undefined : json['correlation_metadata'],
        'metadata': json['metadata'] == null ? undefined : json['metadata'],
        'sourceId': json['source_id'] == null ? undefined : json['source_id'],
        'sourceTechnology': json['source_technology'] == null ? undefined : json['source_technology'],
        'created': json['created'] == null ? undefined : (new Date(json['created'])),
        'originalCreatedTime': json['original_created_time'] == null ? undefined : (new Date(json['original_created_time'])),
        'assignedTime': json['assigned_time'] == null ? undefined : (new Date(json['assigned_time'])),
        'closedTime': json['closed_time'] == null ? undefined : (new Date(json['closed_time'])),
        'escalatedTime': json['escalated_time'] == null ? undefined : (new Date(json['escalated_time'])),
        'notifySLATimer': json['notifySLATimer'] == null ? undefined : json['notifySLATimer'],
        'detectionSLATimer': json['detectionSLATimer'] == null ? undefined : json['detectionSLATimer'],
        'assignmentSLATimer': json['assignmentSLATimer'] == null ? undefined : json['assignmentSLATimer'],
        'remediationSLATimer': json['remediationSLATimer'] == null ? undefined : json['remediationSLATimer'],
        'durationSLATimer': json['durationSLATimer'] == null ? undefined : json['durationSLATimer'],
        'closeReason': json['close_reason'] == null ? undefined : json['close_reason'],
        'closeRationale': json['close_rationale'] == null ? undefined : json['close_rationale'],
        'feedback': json['feedback'] == null ? undefined : json['feedback'],
        'timelineEventCreator': json['timeline_event_creator'] == null ? undefined : json['timeline_event_creator'],
        'events': json['events'] == null ? undefined : ((json['events'] as Array<any>).map(CreateCaseEventRequestFromJSON)),
    };
}

export function UpdateCaseRequestToJSON(json: any): UpdateCaseRequest {
    return UpdateCaseRequestToJSONTyped(json, false);
}

export function UpdateCaseRequestToJSONTyped(value?: UpdateCaseRequest | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'name': value['name'],
        'summary': value['summary'],
        'severity': value['severity'],
        'status': value['status'],
        'owner': value['owner'],
        'escalated': value['escalated'],
        'entities': value['entities'] == null ? undefined : ((value['entities'] as Array<any>).map(CaseEntityDtoToJSON)),
        'custom_fields': value['customFields'],
        'sources': value['sources'] == null ? undefined : ((value['sources'] as Array<any>).map(CaseSourceDtoToJSON)),
        'correlation_group': value['correlationGroup'],
        'correlation_metadata': value['correlationMetadata'],
        'metadata': value['metadata'],
        'source_id': value['sourceId'],
        'source_technology': value['sourceTechnology'],
        'created': value['created'] == null ? undefined : ((value['created']).toISOString()),
        'original_created_time': value['originalCreatedTime'] == null ? undefined : ((value['originalCreatedTime']).toISOString()),
        'assigned_time': value['assignedTime'] == null ? undefined : ((value['assignedTime']).toISOString()),
        'closed_time': value['closedTime'] == null ? undefined : ((value['closedTime']).toISOString()),
        'escalated_time': value['escalatedTime'] == null ? undefined : ((value['escalatedTime']).toISOString()),
        'notifySLATimer': value['notifySLATimer'],
        'detectionSLATimer': value['detectionSLATimer'],
        'assignmentSLATimer': value['assignmentSLATimer'],
        'remediationSLATimer': value['remediationSLATimer'],
        'durationSLATimer': value['durationSLATimer'],
        'close_reason': value['closeReason'],
        'close_rationale': value['closeRationale'],
        'feedback': value['feedback'],
        'timeline_event_creator': value['timelineEventCreator'],
        'events': value['events'] == null ? undefined : ((value['events'] as Array<any>).map(CreateCaseEventRequestToJSON)),
    };
}

